<template>
  <div class="message-compose" ref="message-thread-form">
    <upload-manager
      :upload-key="uploadKey"
      :uploaded-files="uploadedFiles"
      classes="border-bottom"
      @uploaded="handleUploaded"
      @removeUploaded="handleRemoveUploaded"
    />

    <div
      v-if="isMentionPanelOpen && mentionableMembers.length"
      class="mentions w-100"
      :style="{ bottom: textareaHeight }"
    >
      <template v-if="mentionableMembers">
        <div
          v-for="(mentionable, index) in mentionableMembers"
          :key="mentionable.member.id"
          :class="[
            { '--selected': mentionHighlightedIndex === index },
            'd-flex align-items-center mention__subscriber p-1'
          ]"
          @mouseover="mentionHighlightedIndex = index"
          @mousedown="e => e.preventDefault()"
          @click="() => handleMention(mentionable)"
        >
          <subscriber-avatar
            class="mr-1"
            x-small
            :subscriber="{
              name: mentionable.member.name,
              email: mentionable.member.email,
              avatar_urls: mentionable.member.avatar_urls
            }"
          />
          <div>
            <h4 class="mention__subscriber-name my-0">
              {{ mentionable.member.name }}
            </h4>
          </div>
        </div>
      </template>
      <template v-else-if="isLoading">
        <div class="d-flex align-items-center justify-content-center w-100">
          <v-loader />
        </div>
      </template>
      <template v-else>
        {{ $t("components.messageThread.newConversation.memberNotFound") }}
      </template>
    </div>

    <div class="message-compose__control">
      <mentionable
        :keys="[MENTION_SYMBOL]"
        @open="handleMentionOpen"
        @close="handleMentionClose"
        @search="loadMentionableMembers($event)"
      >
        <v-textarea
          ref="textarea"
          :value="message"
          :style="{ height: textareaHeight }"
          :placeholder="placeholder"
          class="message-compose__text"
          :disabled="isLoading"
          :autofocus="$route.query.focus"
          @focus="handleTextareaFocus"
          @blur="handleTextareaBlur"
          @input="handleTextareaInput"
          @keyup="handleTextareaKeyup"
          @enter="handleTextareaEnter"
        />
      </mentionable>

      <v-button round ghost class="mr-n1" @click="chooseFile">
        <v-icon icon="image" size="18px" />
      </v-button>
      <v-button round ghost :is-loading="isLoading" @click="submit">
        <v-icon icon="send" size="18px" />
      </v-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import EventBus, { EVENTS } from "@/lib/event-bus"

import { isBuilderContext, isIos, isMobile } from "@/lib/is-platform"
import { MESSAGE_TYPES } from "@/lib/pusher-utils"
import { isPresent } from "@/lib/utils"

import {
  generatedMessagesFromLinkText,
  MENTION_DELIMITER,
  MENTION_OPENER,
  MENTION_REGEX,
  MENTION_SYMBOL
} from "@/lib/messages-utils"

import vTextarea from "@/components/forms/vTextarea"
import Mentionable from "@/components/message-thread/Mentionable"
import UploadManager from "@/components/UploadManager"

const INITIAL_TEXT_AREA_HEIGHT = 44
const INITIAL_TEXT_AREA_HEIGHT_PX = `${INITIAL_TEXT_AREA_HEIGHT}px`

export default {
  components: {
    vTextarea,
    UploadManager,
    Mentionable,
    SubscriberAvatar: () => import("@/components/SubscriberAvatar")
  },

  props: {
    threadId: { type: [Number, String], default: null },
    isLoading: { type: Boolean, default: false },
    placeholder: { type: String, default: "Write something..." }
  },

  data() {
    return {
      message: "",
      uploadKey: this._uid,
      uploadedFiles: [],

      isMentionPanelOpen: false,
      isFetchingMentions: false,
      mentionableMembers: [],
      mentions: [],
      mentionSearchTerm: "",
      mentionHighlightedIndex: 0,
      mentionWasOpen: false,

      textareaHeight: INITIAL_TEXT_AREA_HEIGHT_PX,

      MENTION_SYMBOL
    }
  },

  computed: {
    ...mapGetters("messages", ["getReplyingMessage", "getMessageThread"]),
    ...mapGetters("auth", ["isAuthenticated"]),

    replyingMessage() {
      return this.getReplyingMessage(this.threadId)
    },

    uploadedImages() {
      return this.uploadedFiles.filter(
        file => file.type && file.type.startsWith("image/")
      )
    },

    uploadedVideos() {
      return this.uploadedFiles.filter(
        file => file.type && file.type.startsWith("video/")
      )
    },

    uploadedImageUrls() {
      return this.uploadedImages.map(file => file.url)
    },

    uploadedImageIds() {
      return this.uploadedImages.map(file => file.signed_id)
    }
  },

  methods: {
    ...mapActions("messages", ["fetchMentionableMembers"]),

    submit() {
      if (isBuilderContext()) return
      if (!this.isAuthenticated) return

      let messages = []
      const createdAt = +new Date()

      if (this.message.length > 0) {
        messages = generatedMessagesFromLinkText({
          text: this.message,
          createdAt
        })

        messages.map(message => {
          // Assign the mentioned members to the regular message
          if (message.messageType === MESSAGE_TYPES.MESSAGE) {
            message.mentionedMembers = this.mentions.map(
              mention => mention.member.id
            )
          }
        })
      }

      if (this.uploadedImages.length > 0) {
        messages.push({
          imageUrls: this.uploadedImageUrls,
          imageIds: this.uploadedImageIds,
          messageType: MESSAGE_TYPES.IMAGE,
          messageCreatedAt: createdAt + messages.length
        })
      }

      this.uploadedVideos.forEach(video => {
        messages.push({
          videoUrl: video.url,
          messageType: MESSAGE_TYPES.VIDEO,
          messageCreatedAt: createdAt + messages.length
        })
      })

      if (messages.length > 0) {
        this.$emit("submit", messages)
        this.resetForm()
      }
    },

    handleTextareaKeyup(e) {
      if (this.isMentionPanelOpen) {
        if (e.key === "ArrowDown" || e.keyCode === 40) {
          this.cancelEvent(e)

          this.mentionHighlightedIndex--

          if (this.mentionHighlightedIndex < 0) {
            this.mentionHighlightedIndex = this.mentionableMembers.length - 1
          }
        } else if (e.key === "ArrowUp" || e.keyCode === 38) {
          this.cancelEvent(e)

          this.mentionHighlightedIndex++

          if (this.mentionHighlightedIndex >= this.mentionableMembers.length) {
            this.mentionHighlightedIndex = 0
          }
        }
      } else if (e.key === "@") {
        // If typing @ immediately after mentioning someone the <Mentionable>
        // component doesn't re-open the panel
        this.handleMentionOpen()
      }
    },

    handleTextareaBlur() {
      if (this.isMentionPanelOpen) {
        this.isMentionPanelOpen = false
        this.mentionWasOpen = true
      }

      this.$emit("blur")
    },

    handleTextareaInput(value, el) {
      let newValue = value
      let removedText = 0

      const existingMentions = this.mentions.map(mention => mention.value)
      const newMentions = value.match(MENTION_REGEX) || []

      if (existingMentions.length != newMentions.length) {
        // Remove mentions from message as well as state list
        this.mentions = this.mentions.filter(mention => {
          if (newMentions.includes(mention.value)) {
            return true
          } else {
            const mentionText = mention.value
              .split(MENTION_OPENER)[1]
              .split(MENTION_DELIMITER)[0]

            removedText = `${MENTION_SYMBOL}${MENTION_OPENER}${mentionText}`

            newValue = newValue.replace(removedText, "@")

            return false
          }
        })
      }

      const newCursorIndex = removedText.length
        ? el.selectionEnd - removedText.length + 1
        : 0

      this.message = newValue

      this.$nextTick(() => {
        if (newCursorIndex) {
          // Ensure cursor stays in correct position if deleting mentions
          el.selectionEnd = newCursorIndex
        }

        this.textareaHeight =
          (this.message
            ? Math.max(el.scrollHeight, INITIAL_TEXT_AREA_HEIGHT)
            : INITIAL_TEXT_AREA_HEIGHT) + "px"
      })

      this.$emit("resetTimer")
    },

    handleTextareaFocus() {
      this.$emit("focus")
      this.$emit("click") // Required for FB Livestream mobile
      this.$emit("resetTimer")

      if (!this.isAuthenticated) {
        this.$router.push("#auth")
      } else if (this.mentionWasOpen) {
        this.isMentionPanelOpen = true
      }
    },

    handleTextareaEnter(e) {
      if (this.isMentionPanelOpen) {
        this.cancelEvent(e)
        this.handleMention(
          this.mentionableMembers[this.mentionHighlightedIndex || 0]
        )
        return
      } else if (isMobile() || isIos()) {
        return
      } else {
        this.cancelEvent(e)
        this.submit()
      }
    },

    cancelEvent(e) {
      e.preventDefault()
      e.stopPropagation()
    },

    handleMentionOpen() {
      this.isMentionPanelOpen = true

      // If re-opening mentions with partial search, both this and the @search
      // event are fired. We want to ensure just 1 API call gets made
      setTimeout(() => {
        if (!this.isFetchingMentions) {
          this.loadMentionableMembers()
        }
      }, 10)
    },

    handleMentionClose() {
      this.isMentionPanelOpen = false
      this.mentionWasOpen = false
    },

    resetForm() {
      this.message = ""
      this.uploadedFiles = []
      this.mentions = []
      this.mentionableMembers = []
      this.textareaHeight = INITIAL_TEXT_AREA_HEIGHT_PX
      this.$emit("resetForm")
    },

    chooseFile() {
      EventBus.$emit(EVENTS.OPEN_FILE_DIALOG, { key: this.uploadKey })
    },

    handleUploaded(file) {
      this.uploadedFiles.push(file)
    },

    handleRemoveUploaded(file) {
      this.uploadedFiles = this.uploadedFiles.filter(
        f => f.signed_id != file.signed_id || f.type != file.type
      )
    },

    handleMention(mention) {
      const value = this.buildMentionValue(mention.member.name)

      this.message = this.message.replace(
        new RegExp(
          `${MENTION_SYMBOL}(?!${MENTION_OPENER}| )${this.mentionSearchTerm}`
        ),
        value + " "
      )

      this.mentions.push({ member: mention.member, value })

      this.isMentionPanelOpen = false

      this.handleTextareaInput(this.message, this.$refs["textarea"].$el)
    },

    getFirstName(fullName) {
      return isPresent(fullName) ? fullName.split(" ")[0] : ""
    },

    loadMentionableMembers(term = "") {
      if (!this.threadId) return // TODO: Make it work for new threads

      this.mentionSearchTerm = term
      this.isFetchingMentions = true

      this.fetchMentionableMembers({
        term,
        messageThreadId: this.threadId
      })
        .then(members => {
          this.mentionableMembers = members.map(member => {
            return {
              member,
              value: this.buildMentionValue(member.name).replace(
                MENTION_SYMBOL,
                ""
              )
            }
          })
          this.mentionHighlightedIndex = 0
        })
        .finally(() => {
          this.isFetchingMentions = false
        })
    },

    buildMentionValue(name) {
      return `${MENTION_SYMBOL}${MENTION_OPENER}${name}${MENTION_DELIMITER}`
    }
  },

  watch: {
    isLoading: {
      immediate: true,
      handler() {
        if (!this.isLoading && this.$route.query.focus) {
          this.$nextTick(() => {
            this.$refs["textarea"].$el.focus()
          })
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

$search-input--border-radius: 0.375rem;

.message-compose {
  .message-compose__control {
    display: flex;
    align-items: center;
  }

  .subscriber-mentions {
    flex-grow: 1;

    .vs__dropdown-toggle {
      display: none;
    }
  }

  .v-textarea.message-compose__text {
    font-family: var(--body-font-stack);
    font-size: 1rem;
    resize: none;
    transition: background 0.2s ease, height 0.2s ease;
    min-height: $size--4;
    height: $thread--text-box-height;
    max-height: $thread--text-box-max-height;
    border-color: transparent;

    &:focus {
      border-color: transparent;
    }
  }

  .message-compose__button {
    background: none;
    border: none;
    padding-left: 1rem;
  }
}

.mentions {
  @include hide-scroll-bars;

  position: absolute;
  display: flex;
  flex-direction: column-reverse;
  bottom: $size--10;
  max-height: $size--14;
  overflow-y: scroll;
  border: 2px solid var(--primary-brand-color-hex);
  border-bottom: 0;
  border-radius: var(--input-border-radius);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  width: calc(100% + 4px);
  left: -2px;
  z-index: 5;
  background: $color-ui--white;
}

.mention__subscriber {
  &.--selected {
    background: $color-ui--grey-15;
  }

  .mention__subscriber-name {
    font-size: 1rem !important;
    line-height: 1.125rem;
    font-weight: 700;
  }
}
</style>
